import React, { useState, useEffect } from "react";
import { useApi, apiPut } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";

interface Project {
  id: number;
  name: string;
  status: string;
  companyName: string;
}

interface Person {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface RoleAssignment {
  role: string;
  person_id: number;
  first_name: string;
  last_name: string;
}

const ROLES = ["pm", "ux", "discovery", "scoping", "ui", "dev", "implementation", "qa"];

export function ProjectList() {
  const { data: projects, loading, error } = useApi<Project[]>("/api/projects");
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  if (loading) return <div className="loading">Loading projects...</div>;

  if (error) {
    try {
      const parsed = JSON.parse(error);
      if (parsed.error === "api_key_missing") {
        return (
          <div className="card">
            <div className="empty-state">
              <p>Teamwork API key not configured.</p>
              <p style={{ marginTop: "8px" }}>
                Add your Teamwork API key above to manage projects.
              </p>
            </div>
          </div>
        );
      }
    } catch {}
    return <div className="card" style={{ color: "var(--color-danger)" }}>Error: {error}</div>;
  }

  const filtered = projects?.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.companyName.toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2>Projects</h2>
        </div>
        <div style={{ marginBottom: "12px" }}>
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", maxWidth: "300px", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: "var(--radius)", fontSize: "14px" }}
          />
        </div>
        <table>
          <thead>
            <tr>
              <th>Project</th>
              <th>Company</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered?.map((project) => (
              <tr key={project.id}>
                <td>{project.name}</td>
                <td>{project.companyName}</td>
                <td>
                  <span className={`badge ${project.status === "active" ? "badge-success" : "badge-neutral"}`}>
                    {project.status}
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() =>
                      setSelectedProject(
                        selectedProject === project.id ? null : project.id
                      )
                    }
                  >
                    {selectedProject === project.id ? "Close" : "Settings"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProject && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Project Settings</h2>
              <button className="btn btn-sm btn-secondary" onClick={() => setSelectedProject(null)}>
                &times;
              </button>
            </div>
            <h3 style={{ margin: "0 0 8px 0", fontSize: "15px" }}>Role Assignments</h3>
            <RoleAssignmentPanel projectId={selectedProject} />
            <hr style={{ margin: "20px 0", border: "none", borderTop: "1px solid var(--color-border)" }} />
            <h3 style={{ margin: "0 0 8px 0", fontSize: "15px" }}>GitHub Repository</h3>
            <GitHubRepoPanel projectId={selectedProject} />
          </div>
        </div>
      )}
    </div>
  );
}

function RoleAssignmentPanel({ projectId }: { projectId: number }) {
  const { data: people } = useApi<Person[]>(`/api/projects/${projectId}/people`);
  const { data: assignments, refetch } = useApi<RoleAssignment[]>(
    `/api/projects/${projectId}/role-assignments`
  );
  const [editing, setEditing] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (assignments) {
      const map: Record<string, number> = {};
      for (const a of assignments) {
        map[a.role] = a.person_id;
      }
      setEditing(map);
    }
  }, [assignments]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const body = Object.entries(editing)
        .filter(([, personId]) => personId > 0)
        .map(([role, personId]) => ({ role, personId }));
      await apiPut(`/api/projects/${projectId}/role-assignments`, body);
      refetch();
      setSaved(true);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th>Assigned To</th>
          </tr>
        </thead>
        <tbody>
          {ROLES.map((role) => (
            <tr key={role}>
              <td style={{ textTransform: "uppercase", fontWeight: 600 }}>
                {role}
              </td>
              <td>
                <SearchSelect
                  options={people?.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` })) || []}
                  value={editing[role] || 0}
                  onChange={(val) => setEditing((prev) => ({ ...prev, [role]: val }))}
                  placeholder="-- Unassigned --"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: "16px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px" }}>
        {saveError && <span style={{ color: "var(--color-danger)", fontSize: "13px" }}>{saveError}</span>}
        {saved && <span style={{ color: "var(--color-success, #22c55e)", fontSize: "13px" }}>Saved!</span>}
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

interface GitHubRepo {
  fullName: string;
  name: string;
  owner: string;
}

interface LinkedRepo {
  repoOwner: string;
  repoName: string;
}

function GitHubRepoPanel({ projectId }: { projectId: number }) {
  const { data: linkedRepo, refetch } = useApi<LinkedRepo | null>(
    `/api/projects/${projectId}/github-repo`
  );
  const { data: repos, error: reposError } = useApi<GitHubRepo[]>("/api/github/repos");
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleLink = async (fullName: string) => {
    if (!fullName) return;
    setSaving(true);
    setSaved(false);
    try {
      const [owner, name] = fullName.split("/");
      await apiPut(`/api/projects/${projectId}/github-repo`, {
        repoOwner: owner,
        repoName: name,
      });
      setSaved(true);
      setSelected("");
      refetch();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiPut(`/api/projects/${projectId}/github-repo`, null);
      setSaved(true);
      refetch();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (reposError) {
    try {
      const parsed = JSON.parse(reposError);
      if (parsed.error === "api_key_missing") {
        return (
          <p style={{ color: "var(--color-text-muted)", fontSize: "13px" }}>
            Add a GitHub token in the API Keys section above to link a repository for codebase-aware estimates.
          </p>
        );
      }
    } catch {}
  }

  return (
    <div>
      {linkedRepo ? (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span>
            Linked: <strong>{linkedRepo.repoOwner}/{linkedRepo.repoName}</strong>
          </span>
          <button className="btn btn-sm btn-secondary" onClick={handleUnlink} disabled={saving}>
            Unlink
          </button>
          {saved && <span style={{ color: "var(--color-success, #22c55e)", fontSize: "13px" }}>Saved!</span>}
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <SearchSelect<string>
              options={repos?.map((r) => ({ value: r.fullName, label: r.fullName })) || []}
              value={selected}
              onChange={(val) => {
                setSelected(val);
                if (val) handleLink(val);
              }}
              emptyValue=""
              placeholder="-- Select a repository --"
              style={{ maxWidth: "400px", flex: 1 }}
            />
            {saved && <span style={{ color: "var(--color-success, #22c55e)", fontSize: "13px" }}>Saved!</span>}
          </div>
          <p style={{ color: "var(--color-text-muted)", fontSize: "12px", marginTop: "4px" }}>
            Linking a repo enables codebase-aware hour estimates when generating tasks.
          </p>
        </div>
      )}
    </div>
  );
}
